'use client';

import { useTranslations } from 'next-intl';
import React, { type ChangeEventHandler, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Input } from '@/app/components/ui/input';
import type { InputRefType } from '@/app/types';
import { cn } from '@/app/utils';

export const InputField = React.forwardRef<
  InputRefType,
  {
    placeholder?: string;
    onChange?: ChangeEventHandler<HTMLInputElement | null>;
    checking?: boolean;
    checkError?: Error;
    value?: string;
    label?: string;
    initialValue?: string;
    maxLength?: number;
    labelClassName?: string;
    fieldClassName?: string;
    errMsg?: string;
    className?: string;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    readOnly?: boolean;
    required?: boolean;
  }
>(
  (
    {
      onChange,
      placeholder,
      label,
      maxLength,
      readOnly = false,
      labelClassName,
      checking,
      checkError,
      fieldClassName,
      errMsg,
      initialValue,
      className,
      onBlur,
      value,
      required,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const t = useTranslations('elements');

    const [inputVal, setInputVal] = useState(initialValue);

    useImperativeHandle(ref, () => ({
      getInputValue: () => inputRef.current?.value,
    }));

    const lenOverLimit = useMemo(() => {
      return maxLength && (inputVal?.length ?? 0) > (maxLength ?? 0);
    }, [inputVal, maxLength]);

    const errorMsg = useMemo(() => {
      if (lenOverLimit) {
        return t('elementDialog.nameLimitStr');
      }
      return errMsg;
    }, [errMsg, lenOverLimit, checkError?.cause]);

    return (
      <div className={className}>
        {label && (
          <div className={cn('mb-2 flex justify-between text-white text-xs', labelClassName)}>
            {label}
            {errorMsg && <div className="text-system-danger">{errorMsg}</div>}
          </div>
        )}
        {readOnly ? (
          <CustomTooltip
            content={value}
            side="bottom"
            variant="extend"
            trigger="auto"
            showOnlyOnOverflow={true}
            maxLines={1}
          >
            <span className="font-medium text-base">{value}</span>
          </CustomTooltip>
        ) : (
          <div className={cn('relative flex items-center rounded-8', fieldClassName)}>
            <Input
              className={cn('flex-1 border-none bg-transparent text-white placeholder:text-system-white36')}
              ref={inputRef}
              defaultValue={initialValue}
              onChange={(e) => {
                onChange?.(e);
                setInputVal(e.target.value);
              }}
              placeholder={placeholder}
              autoFocus={false}
              required={required}
              onBlur={onBlur}
            />
            {checking && <Loading className="animate-spin" />}

            {maxLength && (
              <div className="flex gap-1.5 px-3 py-2 text-gray-500 text-sm">
                <div className={cn(inputVal?.length ? (lenOverLimit ? 'text-system-danger' : 'text-white') : '')}>
                  {inputVal?.length || 0}
                </div>
                / <div className="text-white opacity-40">{maxLength}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
